import React, { useState, useEffect } from 'react';
import { Alert, Box, IconButton, Snackbar, Tab, Tabs, Tooltip } from '@mui/material';
import { RotateCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { apiSlice } from '../store/api/apiSlice';
import { useGetDepartmentsQuery } from '../store/slices/departmentSlice';
import { useGetEmployeesQuery } from '../store/slices/employeeSlice';
import {
  useGetFilteredTimeOffEntriesQuery,
  useAddTimeOffEntryMutation,
  useUpdateTimeOffEntryMutation,
  TimeOffEntry,
  TimeOffType,
  TimeOffStatus
} from '../store/slices/timeOffSlice';
import { useAuth } from '../contexts/AuthContext';
import {
  format,
  parseISO,
  startOfDay,
  endOfDay,
  isWithinInterval,
  isAfter,
  isBefore,
  addDays,
  differenceInCalendarDays,
  startOfYear,
  endOfYear,
} from 'date-fns';
import OOOReconciler from '../components/calendar/OOOReconciler';
import TimeOffCalendarView from '../components/calendar/TimeOffCalendarView';
import { CountBadge, ErrorState, PageHeader, SectionCard } from '../components/ui';
import { isManagerLike, scopesEmployee } from '../utils/roles';
import { formatDate, formatDateRange, formatTime } from '../utils/format';
import ApprovalsQueue from './hr-time-off/ApprovalsQueue';
import type { Decision } from './hr-time-off/ApprovalsQueue';
import LogTimeOffPanel from './hr-time-off/LogTimeOffPanel';
import ScheduleTimeOffDialog, { type ScheduleTimeOffErrors } from './hr-time-off/ScheduleTimeOffDialog';
import {
  HOURS_PER_DAY,
  dayOffReason,
  hoursForRange,
  quickLogDay,
  validateTimeOffHours,
  validateTimeOffRange,
} from '../components/modals/time-off/timeOffHours';
import { savedStatus } from '../components/modals/time-off/saveTimeOffRequest';
import TeamOverviewPanel from './hr-time-off/TeamOverviewPanel';
import UnmatchedEventsCard from './hr-time-off/UnmatchedEventsCard';
import {
  RegionSkeleton,
  TimeOffFormData,
  NO_DEPARTMENT_LABEL,
  UNASSIGNED_DEPARTMENT_ID,
  buildScheduleBody,
  possessive,
  queueNoteFor,
  typeLabel,
  typePhrase,
} from './hr-time-off/shared';

const CALENDAR_ROUTE = '/hr-time-off/calendar';

const complianceReviewReasons = new Set(['pto_lead_time', 'wfh_not_approved']);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hr-tabpanel-${index}`}
      aria-labelledby={`hr-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `hr-tab-${index}`,
    'aria-controls': `hr-tabpanel-${index}`,
  };
}

/** A tab label with a neutral count badge ("Approvals 3"). */
const TabLabel: React.FC<{ label: string; count?: number }> = ({ label, count }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {label}
    {typeof count === 'number' && count > 0 && (
      <>
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex' }}>
          <CountBadge count={count} />
        </Box>
        <span className="sbg-sr-only">{`, ${count} waiting`}</span>
      </>
    )}
  </Box>
);

const HRTimeOffManager: React.FC = (): React.ReactElement => {
  const { profile, user } = useAuth();
  const role = profile?.role;
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // State for filters
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // State for tabs
  const [tabValue, setTabValue] = useState(() => (
    location.pathname === CALENDAR_ROUTE ? 2 : 0
  ));

  // Both routes render this same component, so React keeps its state between
  // them. Entering the calendar route selects the Calendar tab; leaving it for
  // /hr-time-off opens on Approvals again instead of the tab it left behind.
  const previousPathRef = React.useRef(location.pathname);
  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = location.pathname;
    if (location.pathname === CALENDAR_ROUTE) {
      setTabValue(2);
    } else if (previousPath === CALENDAR_ROUTE) {
      setTabValue(0);
    }
  }, [location.pathname]);

  // State for time off form
  const [openTimeOffDialog, setOpenTimeOffDialog] = useState<boolean>(false);
  const [scheduleErrors, setScheduleErrors] = useState<ScheduleTimeOffErrors>({});
  const [timeOffFormData, setTimeOffFormData] = useState<TimeOffFormData>({
    employeeId: '',
    startDate: new Date(),
    endDate: new Date(),
    type: TimeOffType.PTO,
    hours: 8,
    notes: '',
  });
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [pendingQuickLog, setPendingQuickLog] = useState<string | null>(null);

  // State for employee table pagination
  const [empPage, setEmpPage] = useState(0);
  const empRowsPerPage = 25;

  // State for snackbar
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const notify = React.useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Fetch data — only load current year time off entries to reduce payload
  const currentYearStart = React.useMemo(() => format(startOfYear(new Date()), 'yyyy-MM-dd'), []);
  const {
    data: departments = [],
    isLoading: isLoadingDepartments,
    isError: isDepartmentsError,
    refetch: refetchDepartments,
  } = useGetDepartmentsQuery();
  const {
    data: employees = [],
    isLoading: isLoadingEmployees,
    isError: isEmployeesError,
    refetch: refetchEmployees,
  } = useGetEmployeesQuery(
    undefined,
    { skip: isManagerLike(role) }
  );
  const {
    data: timeOffEntries = [],
    isLoading: isLoadingTimeOff,
    isFetching: isFetchingTimeOff,
    isError: isTimeOffError,
    refetch: refetchTimeOff,
    fulfilledTimeStamp: timeOffUpdatedAt,
  } = useGetFilteredTimeOffEntriesQuery({ startDate: currentYearStart });

  // The HR/admin calendar loads every year through its own query. Subscribing
  // to the same cache entry (no extra request) lets the header report when the
  // calendar on screen was last updated.
  const showsFullCalendar = !isManagerLike(role) && (location.pathname === CALENDAR_ROUTE || tabValue === 2);
  const {
    isLoading: isLoadingCalendar,
    isFetching: isFetchingCalendar,
    fulfilledTimeStamp: calendarUpdatedAt,
  } = useGetFilteredTimeOffEntriesQuery({}, { skip: !showsFullCalendar });

  // Mutations
  const [addTimeOffEntry, { isLoading: isAddingTimeOff }] = useAddTimeOffEntryMutation();
  const [updateTimeOffEntry, { isLoading: isUpdatingTimeOff }] = useUpdateTimeOffEntryMutation();

  const managerDepartmentIds = React.useMemo(() => {
    if (role !== 'manager' || !user?.id) return [];
    return departments.filter((dept) => dept.managerUserId === user.id).map((dept) => dept._id);
  }, [departments, role, user?.id]);

  const managerTeamEntries = React.useMemo(() => {
    if (!isManagerLike(role)) return timeOffEntries;
    return timeOffEntries.filter((entry) => scopesEmployee(entry.employee, {
      role,
      userId: user?.id,
      managerDepartmentIds,
    }));
  }, [timeOffEntries, role, user?.id, managerDepartmentIds]);

  const approvalEntries = React.useMemo(() => {
    return managerTeamEntries.filter((entry) => {
      if (entry.status !== TimeOffStatus.PENDING) return false;
      const isPto = entry.type === TimeOffType.PTO;
      const isWfhOverride = entry.type === TimeOffType.WFH && entry.needsReview;
      if (!isPto && !isWfhOverride) return false;
      return true;
    });
  }, [managerTeamEntries]);

  const today = React.useMemo(() => new Date(), []);
  const todayStart = startOfDay(today);

  // Build a set of active employee IDs so terminated/resigned people never
  // appear in "active today" lists, team coverage, or any other live count.
  const activeEmployeeIdSet = React.useMemo(
    () => new Set(
      employees
        .filter((e: any) => !e.employmentStatus || e.employmentStatus === 'active')
        .map((e: any) => e._id)
    ),
    [employees]
  );

  const activeToday = React.useMemo(() => {
    const typePriority: Record<string, number> = {
      [TimeOffType.PTO]: 3,
      [TimeOffType.SICK]: 2,
      [TimeOffType.WFH]: 1,
    };
    const byEmployee = new Map<string, TimeOffEntry>();

    timeOffEntries.forEach((entry) => {
      if (entry.status === TimeOffStatus.REJECTED) return;
      if (entry.hours === 0) return;
      const start = startOfDay(parseISO(entry.startDate));
      const end = endOfDay(parseISO(entry.endDate));
      if (!isWithinInterval(today, { start, end })) return;

      const employeeId = entry.employee?._id;
      if (!employeeId) return;
      if (!activeEmployeeIdSet.has(employeeId)) return;

      const existing = byEmployee.get(employeeId);
      if (!existing) {
        byEmployee.set(employeeId, entry);
        return;
      }

      const existingPriority = typePriority[existing.type] ?? 0;
      const nextPriority = typePriority[entry.type] ?? 0;
      if (nextPriority > existingPriority) {
        byEmployee.set(employeeId, entry);
        return;
      }

      if (nextPriority === existingPriority) {
        const existingDays = differenceInCalendarDays(parseISO(existing.endDate), parseISO(existing.startDate)) + 1;
        const nextDays = differenceInCalendarDays(parseISO(entry.endDate), parseISO(entry.startDate)) + 1;
        if (nextDays > existingDays) {
          byEmployee.set(employeeId, entry);
        }
      }
    });

    return Array.from(byEmployee.values());
  }, [timeOffEntries, today, activeEmployeeIdSet]);

  const teamCoverage = React.useMemo(() => {
    const teamMap = new Map<
      string,
      { teamName: string; total: number; outToday: number; outNames: Set<string> }
    >();

    const getTeamName = (employee: { teamName?: string | null; department?: { name?: string } | null }) =>
      employee.teamName || employee.department?.name || NO_DEPARTMENT_LABEL;

    employees.forEach((employee: any) => {
      // Skip terminated or resigned employees so they do not inflate team
      // headcount or coverage percentages.
      if (employee.employmentStatus && employee.employmentStatus !== 'active') return;
      const teamName = getTeamName(employee);
      if (!teamMap.has(teamName)) {
        teamMap.set(teamName, { teamName, total: 0, outToday: 0, outNames: new Set() });
      }
      teamMap.get(teamName)!.total += 1;
    });

    activeToday.forEach((entry) => {
      const teamName = getTeamName(entry.employee);
      if (!teamMap.has(teamName)) {
        teamMap.set(teamName, { teamName, total: 0, outToday: 0, outNames: new Set() });
      }
      const bucket = teamMap.get(teamName)!;
      bucket.outNames.add(entry.employee.name);
    });

    return Array.from(teamMap.values()).map((team) => {
      const outToday = team.outNames.size;
      return {
        teamName: team.teamName,
        total: team.total,
        outToday,
        outNames: Array.from(team.outNames),
        coverage: team.total > 0 ? Math.round(((team.total - outToday) / team.total) * 100) : 100,
      };
    });
  }, [employees, activeToday]);

  const complianceIssues = React.useMemo(() => {
    return timeOffEntries.filter(
      (entry) => entry.needsReview && entry.reviewReason && complianceReviewReasons.has(entry.reviewReason)
    );
  }, [timeOffEntries]);

  const upcomingLongPto = React.useMemo(() => {
    const windowStart = addDays(todayStart, 1);
    const windowEnd = addDays(todayStart, 30);
    return timeOffEntries
      .filter((entry) => entry.type === TimeOffType.PTO && entry.status !== TimeOffStatus.REJECTED)
      .filter((entry) => {
        const start = parseISO(entry.startDate);
        return start >= windowStart && start <= windowEnd;
      })
      .filter((entry) => differenceInCalendarDays(parseISO(entry.endDate), parseISO(entry.startDate)) + 1 >= 3);
  }, [timeOffEntries, todayStart]);

  const ptoPacingOver = React.useMemo(() => {
    const yearStart = startOfYear(today);
    const yearEnd = endOfYear(today);
    const daysInYear = differenceInCalendarDays(yearEnd, yearStart) + 1;
    const daysElapsed = differenceInCalendarDays(today, yearStart) + 1;
    const ratio = daysElapsed / daysInYear;

    const usedByEmployee = new Map<string, number>();
    timeOffEntries.forEach((entry) => {
      if (entry.type !== TimeOffType.PTO) return;
      if (entry.status !== TimeOffStatus.APPROVED) return;
      if (isAfter(parseISO(entry.startDate), todayStart)) return;
      if (isBefore(parseISO(entry.startDate), yearStart)) return;
      if (isAfter(parseISO(entry.startDate), yearEnd)) return;
      const employeeId = entry.employee?._id;
      if (!employeeId) return;
      usedByEmployee.set(employeeId, (usedByEmployee.get(employeeId) || 0) + entry.hours);
    });

    return employees
      .filter((employee) => employee.ptoAllotment > 0)
      .map((employee) => {
        const used = usedByEmployee.get(employee._id) || 0;
        const expectedUsed = employee.ptoAllotment * ratio;
        const overBy = used - expectedUsed;
        const overPercent = expectedUsed > 0 ? Math.round((used / expectedUsed) * 100) : 0;
        return { employee, expectedUsed, overBy, overPercent, used };
      })
      .filter((row) => row.overBy >= 8 && row.overPercent >= 120)
      .sort((a, b) => b.overBy - a.overBy);
  }, [employees, timeOffEntries, today, todayStart]);

  // Filter employees then group by department
  const filteredEmployees = React.useMemo(() => {
    return employees.filter(
      (employee) => {
        const employeeDepartmentId = employee.department?._id || employee.department?.id;
        return (
          (departmentFilter ? employeeDepartmentId === departmentFilter : true) &&
          (searchTerm ? employee.name.toLowerCase().includes(searchTerm.toLowerCase()) : true)
        );
      }
    );
  }, [employees, departmentFilter, searchTerm]);

  // Reset pagination when filters change
  React.useEffect(() => { setEmpPage(0); }, [searchTerm, departmentFilter]);

  // Paginated slice of filtered employees
  const paginatedEmployees = React.useMemo(() => {
    return filteredEmployees.slice(empPage * empRowsPerPage, (empPage + 1) * empRowsPerPage);
  }, [filteredEmployees, empPage, empRowsPerPage]);

  // Group ONLY the paginated employees by department (not ALL employees)
  const employeesByDepartment = React.useMemo(() => {
    return paginatedEmployees.reduce((acc, employee) => {
      const deptId = employee.department?._id || employee.department?.id || UNASSIGNED_DEPARTMENT_ID;
      if (!acc[deptId]) {
        acc[deptId] = [];
      }
      acc[deptId].push(employee);
      return acc;
    }, {} as Record<string, typeof employees>);
  }, [paginatedEmployees]);

  // Handle opening the time off form for an employee
  const handleOpenTimeOffForm = (employee: any) => {
    setSelectedEmployee(employee);
    setScheduleErrors({});
    setTimeOffFormData({
      employeeId: employee._id,
      startDate: new Date(),
      endDate: new Date(),
      type: TimeOffType.PTO,
      hours: 8,
      notes: '',
    });
    setOpenTimeOffDialog(true);
  };

  // Handle closing the time off form
  const handleCloseTimeOffForm = () => {
    setOpenTimeOffDialog(false);
    setSelectedEmployee(null);
  };

  // Handle form field changes
  const handleFormChange = (field: keyof TimeOffFormData, value: any) => {
    setTimeOffFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // A changed field drops its error; new dates also refill the hours, so their error goes too.
    setScheduleErrors((current) => {
      const next = { ...current };
      if (field === 'startDate' || field === 'endDate') {
        delete next.startDate;
        delete next.endDate;
        delete next.hours;
      }
      if (field === 'hours') delete next.hours;
      return next;
    });
  };

  // Refill hours from the dates: 8 per workday, skipping weekends and SBG
  // company holidays, the same rule as calendar imports (time-off/timeOffHours.ts).
  useEffect(() => {
    if (timeOffFormData.startDate && timeOffFormData.endDate) {
      setTimeOffFormData((prev) => ({
        ...prev,
        hours: hoursForRange(prev.startDate, prev.endDate),
      }));
    }
  }, [timeOffFormData.startDate, timeOffFormData.endDate]);

  const resolveCreateStatus = (type: TimeOffType) => {
    if (type === TimeOffType.PTO && role !== 'admin') {
      return TimeOffStatus.PENDING;
    }
    return TimeOffStatus.APPROVED;
  };

  // Handle submitting the time off form
  const handleSubmitTimeOff = async () => {
    try {
      if (!timeOffFormData.employeeId) {
        notify('Choose who this time off is for, then save it.', 'error');
        return;
      }
      // Nothing backwards, half-typed or impossible reaches the API: the
      // dialog shows each problem under its field instead.
      const rangeErrors = validateTimeOffRange(timeOffFormData.startDate, timeOffFormData.endDate);
      const hoursError = rangeErrors.startDate || rangeErrors.endDate
        ? undefined
        : validateTimeOffHours(timeOffFormData.hours, timeOffFormData.startDate, timeOffFormData.endDate);
      const fieldErrors: ScheduleTimeOffErrors = { ...rangeErrors, ...(hoursError ? { hours: hoursError } : null) };
      if (Object.keys(fieldErrors).length > 0) {
        setScheduleErrors(fieldErrors);
        return;
      }

      const timeOffData = buildScheduleBody(timeOffFormData, resolveCreateStatus(timeOffFormData.type));

      const saved = await addTimeOffEntry(timeOffData as unknown as Partial<TimeOffEntry>).unwrap();

      // The API can queue what was sent as approved (short-notice PTO, WFH for
      // in-office staff), so the toast reads the status it stored.
      const queued = (savedStatus(saved) ?? timeOffData.status) !== TimeOffStatus.APPROVED;
      notify(
        `Scheduled ${typePhrase(timeOffFormData.type)} for ${selectedEmployee?.name}, ${formatDateRange(timeOffFormData.startDate, timeOffFormData.endDate)}.${queued ? ' It’s in the approval queue.' : ''}`,
        'success'
      );
      handleCloseTimeOffForm();
    } catch (error) {
      console.error('Failed to add time off:', error);
      notify(`Couldn’t save time off for ${selectedEmployee?.name ?? 'this employee'}. Nothing was saved; try again.`, 'error');
    }
  };

  // One-click logs book today: one workday, the 8 hours the schedule dialog
  // charges for it. Weekends and company holidays have no hours to log.
  const handleQuickTimeOff = async (employee: any, type: TimeOffType) => {
    // Today on the company's calendar (Eastern), not the browser's: after
    // 9 PM Pacific the two differ, and the API keys dates by Eastern days.
    const today = quickLogDay();
    const dayOff = dayOffReason(today.date);
    if (dayOff) {
      notify(`Today is ${dayOff}, so there are no hours to log for ${employee.name}. Use Schedule time off to book workdays.`, 'error');
      return;
    }
    setPendingQuickLog(`${employee._id}:${type}`);
    try {
      // Create time off data with the correct structure for the API
      const timeOffData: any = {
        employee: employee._id, // API expects employee ID as string
        // A calendar date, as the employee form sends them: a UTC timestamp
        // is already tomorrow after 8 PM Eastern.
        startDate: today.key,
        endDate: today.key,
        type,
        hours: HOURS_PER_DAY,
        notes: `Quick ${type} entry for ${formatDate(today.key)}`,
        status: resolveCreateStatus(type),
      };

      const saved = await addTimeOffEntry(timeOffData).unwrap();

      const queued = (savedStatus(saved) ?? timeOffData.status) !== TimeOffStatus.APPROVED;
      notify(
        `Logged ${typePhrase(type)} for ${employee.name} today, ${formatDate(today.key)}.${queued ? ' It’s in the approval queue.' : ''}`,
        'success'
      );
    } catch (error) {
      console.error('Failed to add quick time off:', error);
      notify(`Couldn’t log ${typePhrase(type)} for ${employee.name}. Nothing was saved; try again.`, 'error');
    } finally {
      setPendingQuickLog(null);
    }
  };

  // Approve or decline one request from the review queue.
  const handleDecision = React.useCallback(async (entry: TimeOffEntry, decision: Decision) => {
    const approved = decision === TimeOffStatus.APPROVED;
    const what = `${possessive(entry.employee.name)} ${typeLabel(entry.type)} for ${formatDateRange(entry.startDate, entry.endDate)}`;
    try {
      await updateTimeOffEntry({ id: entry._id, timeOff: { status: decision } }).unwrap();
      notify(`${approved ? 'Approved' : 'Declined'} ${what}.`, 'success');
      return true;
    } catch (error) {
      console.error('Failed to update time off:', error);
      notify(`Couldn’t ${approved ? 'approve' : 'decline'} ${what}. Nothing changed; try again.`, 'error');
      return false;
    }
  }, [updateTimeOffEntry, notify]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const isManager = isManagerLike(role);
  const refreshAll = () => {
    refetchTimeOff();
    // Refetch every time-off list on screen, including the calendar's own query.
    dispatch(apiSlice.util.invalidateTags([{ type: 'TimeOff', id: 'LIST' }]));
    refetchDepartments();
    if (!isManager) refetchEmployees();
  };

  // Header meta: scope · period · freshness (ET), describing the data on screen.
  const periodLabel = `Since ${formatDate(currentYearStart)}`;
  // Freshness describes data already on screen, so nothing shows until the first load lands.
  const freshnessOf = (fetching: boolean, loading: boolean, updatedAt?: number) => {
    if (loading || !updatedAt) return null;
    return fetching ? 'Refreshing…' : `Updated ${formatTime(updatedAt)}`;
  };
  const listFreshness = freshnessOf(isFetchingTimeOff, isLoadingTimeOff, timeOffUpdatedAt);
  const calendarFreshness = freshnessOf(isFetchingCalendar, isLoadingCalendar, calendarUpdatedAt);
  const refreshActionFor = (busy: boolean) => (
    <Tooltip title="Refresh data">
      <span>
        <IconButton aria-label="Refresh data" onClick={refreshAll} disabled={busy} aria-busy={busy || undefined}>
          <RotateCw />
        </IconButton>
      </span>
    </Tooltip>
  );

  const toast = (
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={snackbarSeverity === 'error' ? null : 5000}
      onClose={(_, reason) => {
        if (reason !== 'clickaway') setSnackbarOpen(false);
      }}
    >
      <Alert
        severity={snackbarSeverity}
        role={snackbarSeverity === 'error' ? 'alert' : 'status'}
        onClose={() => setSnackbarOpen(false)}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  );

  // Calendar route (managers always, HR and admins via "Calendar View").
  if (isManager || location.pathname === CALENDAR_ROUTE) {
    const calendarLoading = isManager && (isLoadingTimeOff || isLoadingDepartments);
    const calendarError = isManager && (isTimeOffError || isDepartmentsError);
    return (
      <Box>
        <PageHeader
          overline="Attendance"
          title="Time-off calendar"
          meta={isManager ? ['Your team', periodLabel, listFreshness] : ['All departments', calendarFreshness]}
          actions={refreshActionFor(isManager ? isFetchingTimeOff : isFetchingCalendar)}
        />
        {calendarError ? (
          <SectionCard>
            <ErrorState
              title="Couldn’t load your team’s time off"
              body="The server didn’t respond. Try again in a minute."
              onRetry={refreshAll}
            />
          </SectionCard>
        ) : calendarLoading ? (
          <RegionSkeleton label="Loading the time-off calendar" height="min(70vh, 640px)" />
        ) : isManager ? (
          <TimeOffCalendarView entries={managerTeamEntries} />
        ) : (
          <TimeOffCalendarView />
        )}
        {toast}
      </Box>
    );
  }

  // Calendar: all years, so no period. Reconcile: both cards load their own
  // data and carry their own refresh, so the header claims no freshness there.
  const headerMeta =
    tabValue === 2
      ? ['All departments', calendarFreshness]
      : tabValue === 3
        ? ['All departments']
        : ['All departments', periodLabel, listFreshness];
  const headerAction = tabValue === 3 ? undefined : refreshActionFor(tabValue === 2 ? isFetchingCalendar : isFetchingTimeOff);

  return (
    <Box>
      <PageHeader
        overline="Attendance"
        title="HR reviews"
        meta={headerMeta}
        actions={headerAction}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="HR review sections"
        >
          <Tab label={<TabLabel label="Approvals" count={isLoadingTimeOff ? undefined : approvalEntries.length} />} {...a11yProps(0)} />
          <Tab label="Log time off" {...a11yProps(1)} />
          <Tab label="Calendar" {...a11yProps(2)} />
          <Tab label="Reconcile" {...a11yProps(3)} />
          <Tab label="Team overview" {...a11yProps(4)} />
        </Tabs>
      </PageHeader>

      <TabPanel value={tabValue} index={0}>
        <ApprovalsQueue
          entries={approvalEntries}
          employees={employees}
          today={today}
          loading={isLoadingTimeOff || isLoadingEmployees}
          error={isTimeOffError}
          onRetry={refetchTimeOff}
          onDecide={handleDecision}
          deciding={isUpdatingTimeOff}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <LogTimeOffPanel
          departments={departments}
          employeesByDepartment={employeesByDepartment}
          totalEmployees={employees.length}
          filteredCount={filteredEmployees.length}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          departmentFilter={departmentFilter}
          onDepartmentChange={setDepartmentFilter}
          page={empPage}
          rowsPerPage={empRowsPerPage}
          onPageChange={setEmpPage}
          loading={isLoadingEmployees || isLoadingDepartments}
          error={isEmployeesError}
          onRetry={refetchEmployees}
          onQuickLog={handleQuickTimeOff}
          pendingQuickLog={pendingQuickLog}
          onSchedule={handleOpenTimeOffForm}
        />
        <ScheduleTimeOffDialog
          open={openTimeOffDialog}
          employee={selectedEmployee}
          formData={timeOffFormData}
          onChange={handleFormChange}
          onClose={handleCloseTimeOffForm}
          onSubmit={handleSubmitTimeOff}
          saving={isAddingTimeOff}
          queueNote={queueNoteFor(timeOffFormData.type, timeOffFormData.startDate, {
            isAdmin: role === 'admin',
            wfhEligible: selectedEmployee?.wfhEligible,
          })}
          errors={scheduleErrors}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <TimeOffCalendarView />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          <UnmatchedEventsCard employees={employees} onNotify={notify} />
          <OOOReconciler />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <TeamOverviewPanel
          activeToday={activeToday}
          teamCoverage={teamCoverage}
          complianceIssues={complianceIssues}
          upcomingLongPto={upcomingLongPto}
          ptoPacingOver={ptoPacingOver}
          today={today}
          periodStart={formatDate(currentYearStart)}
          loading={isLoadingEmployees || isLoadingTimeOff}
          error={isEmployeesError || isTimeOffError}
          onRetry={refreshAll}
          onOpenCalendar={() => setTabValue(2)}
        />
      </TabPanel>

      {toast}
    </Box>
  );
};

export default HRTimeOffManager;
